import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, FolderHeart, Eye, Pencil, Trash2, FileText, CheckCircle2, Video, Loader2, Save } from "lucide-react";
import { api, apiErr } from "../api";
import { FullLoader, Empty, Modal } from "../ui";
import MediaField from "../components/MediaField";
import { SPEC } from "../fields";

export default function CasesTab({ patients }) {
  const nav = useNavigate();
  const [cases, setCases] = useState(null);
  const [filter, setFilter] = useState("");
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/cases"); setCases(data); }
    catch (e) { toast.error(apiErr(e)); setCases((c) => c || []); }
  }, []);
  useEffect(() => { load(); }, [load]);
  if (!cases) return <FullLoader />;

  const del = async (c) => {
    if (!window.confirm(`¿Eliminar el caso "${c.title || c.patient_name}"?`)) return;
    try { await api.delete(`/cases/${c.id}`); toast.success("Caso eliminado"); load(); } catch (e) { toast.error(apiErr(e)); }
  };
  const shown = filter ? cases.filter((c) => c.patient_id === filter) : cases;
  const Mark = ({ ok, icon: Icon }) => ok ? <Icon size={18} style={{ color: "var(--ok)" }} /> : <span className="pn-muted">—</span>;

  return (
    <div>
      <p className="pn-sub text-sm mb-4 max-w-3xl">{SPEC.cases.intro} Ideal para sesiones o docencia.</p>
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <select value={filter} onChange={(e) => setFilter(e.target.value)} className="pn-field !w-auto min-w-[220px]">
          <option value="">Todos los pacientes</option>
          {patients.map((p) => <option key={p.id} value={p.id}>{p.nombre}</option>)}
        </select>
        <button onClick={() => setEditing({})} className="pn-btn pn-btn-primary ml-auto" disabled={!patients.length} title={patients.length ? "" : "Primero registra un paciente"}><Plus size={17} /> Nuevo caso</button>
      </div>
      {shown.length === 0 ? <Empty icon={FolderHeart}>No hay casos todavía.</Empty> : (
        <div className="pn-card rounded-2xl overflow-x-auto">
          <table className="pn-table min-w-[820px]">
            <thead><tr><th>Caso</th><th>Paciente</th><th>Labs</th><th>{SPEC.cases.images}</th><th>{SPEC.cases.video}</th><th>Diagnóstico</th><th className="text-right">Acciones</th></tr></thead>
            <tbody>{shown.map((c) => (
              <tr key={c.id} className="pn-row-hover">
                <td className="font-semibold pn-ink">{c.title || "Sin título"}<div className="text-xs pn-muted font-normal">{c.fecha || ""}{c.consulta ? ` · ${c.consulta}` : ""}</div></td>
                <td className="pn-sub">{c.patient_name}</td>
                <td><Mark ok={(c.lab_media || []).length || c.laboratorios} icon={FileText} /></td>
                <td><Mark ok={(c.ekg_media || []).length} icon={CheckCircle2} /></td>
                <td><Mark ok={(c.eco_media || []).length || c.eco_text} icon={Video} /></td>
                <td className="pn-sub text-sm max-w-[220px] truncate">{c.diagnostico || "—"}</td>
                <td><div className="flex justify-end gap-1.5">
                  <button onClick={() => nav(`/caso/${c.id}`)} className="pn-btn pn-btn-soft !px-2.5 !py-1.5 text-xs"><Eye size={14} /> Presentar</button>
                  <button onClick={() => setEditing(c)} className="pn-icon-btn" title="Editar"><Pencil size={15} /></button>
                  <button onClick={() => del(c)} className="pn-icon-btn" title="Eliminar"><Trash2 size={15} /></button>
                </div></td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}
      {editing && <CaseModal initial={editing.id ? editing : null} patients={patients} onClose={() => setEditing(null)} onSaved={load} />}
    </div>
  );
}

function CaseModal({ initial, patients, onClose, onSaved }) {
  const [c, setC] = useState(() => ({
    patient_id: "", title: "", consulta: "", fecha: "", cuadro_clinico: "", laboratorios: "", eco_text: "", diagnostico: "",
    ekg_media: [], eco_media: [], lab_media: [], ...(initial || {}),
  }));
  const [busy, setBusy] = useState(false);
  const up = (k) => (e) => setC((x) => ({ ...x, [k]: e.target.value }));
  const fromPatient = () => {
    const p = patients.find((x) => x.id === c.patient_id);
    if (!p) return;
    setC((x) => ({ ...x, title: x.title || p.titulo_diagnostico || "", consulta: x.consulta || p.consulta_lugar || "", fecha: x.fecha || p.fecha_consulta || "",
      cuadro_clinico: x.cuadro_clinico || p.cuadro_clinico || "", laboratorios: x.laboratorios || p.laboratorios || "", eco_text: x.eco_text || p.eco_descripcion || [p.endoscopia_alta, p.colonoscopia].filter(Boolean).join("\n") || "",
      diagnostico: x.diagnostico || p.titulo_diagnostico || "", ekg_media: x.ekg_media.length ? x.ekg_media : p.ekg_media || [],
      eco_media: x.eco_media.length ? x.eco_media : p.eco_media || [], lab_media: x.lab_media.length ? x.lab_media : p.lab_media || [] }));
    toast.success("Datos copiados de la ficha del paciente");
  };
  const save = async () => {
    if (!c.patient_id) { toast.error("Selecciona un paciente"); return; }
    setBusy(true);
    try {
      if (initial?.id) await api.put(`/cases/${initial.id}`, c); else await api.post("/cases", c);
      toast.success("Caso guardado"); onSaved(); onClose();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <Modal title={initial ? "Editar caso" : "Nuevo caso clínico"} icon={FolderHeart} onClose={onClose} maxWidth="max-w-3xl"
      footer={<><button onClick={onClose} className="pn-btn pn-btn-ghost">Cancelar</button>
        <button onClick={save} disabled={busy} className="pn-btn pn-btn-primary">{busy ? <Loader2 size={16} className="pn-spin" /> : <Save size={16} />} Guardar caso</button></>}>
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="sm:col-span-2 flex gap-2 items-end">
          <div className="flex-1"><label className="pn-label">Paciente *</label>
            <select value={c.patient_id} onChange={up("patient_id")} className="pn-field"><option value="">Selecciona un paciente</option>
              {patients.map((p) => <option key={p.id} value={p.id}>{p.nombre}</option>)}</select></div>
          <button type="button" onClick={fromPatient} disabled={!c.patient_id} className="pn-btn pn-btn-ghost">Copiar de la ficha</button>
        </div>
        <div className="sm:col-span-2"><label className="pn-label">Título del caso</label><input value={c.title} onChange={up("title")} placeholder="Ej. Insuficiencia cardíaca por diabetes" className="pn-field" /></div>
        <div><label className="pn-label">Consulta / lugar</label><input value={c.consulta} onChange={up("consulta")} className="pn-field" /></div>
        <div><label className="pn-label">Fecha</label><input value={c.fecha} onChange={up("fecha")} placeholder="Ej. 28 de octubre 2026" className="pn-field" /></div>
        <div className="sm:col-span-2"><label className="pn-label">Cuadro clínico</label><textarea rows={3} value={c.cuadro_clinico} onChange={up("cuadro_clinico")} className="pn-field" /></div>
        <div><label className="pn-label">Laboratorios</label><textarea rows={3} value={c.laboratorios} onChange={up("laboratorios")} className="pn-field" /></div>
        <div><label className="pn-label">{SPEC.cases.videoText}</label><textarea rows={3} value={c.eco_text} onChange={up("eco_text")} className="pn-field" /></div>
        <div className="sm:col-span-2"><label className="pn-label">Diagnóstico</label><textarea rows={2} value={c.diagnostico} onChange={up("diagnostico")} className="pn-field" /></div>
        <MediaField label={SPEC.media.lab_media.label} accept="application/pdf,image/*" media={c.lab_media} onChange={(m) => setC((x) => ({ ...x, lab_media: m }))} />
        <MediaField label={SPEC.media.ekg_media.label} accept="image/*" media={c.ekg_media} onChange={(m) => setC((x) => ({ ...x, ekg_media: m }))} />
        <MediaField label={SPEC.media.eco_media.label} accept="video/*,image/*" media={c.eco_media} onChange={(m) => setC((x) => ({ ...x, eco_media: m }))} />
      </div>
    </Modal>
  );
}
